<template>
  <div>
    <status-information
      v-if="informationItem"
      :item="informationItem"
    />
    <b-row class="mt-1">
      <b-col cols="6">
        <b-form-group
          label-for="parcel-tracking-number-tree"
        >
          <b-form-tags
            id="parcel-tracking-number-tree"
            v-model="filterDeclarationsByTrackingNumber"
            label="Parcel tracking numbers"
            :input-attrs="{ 'aria-describedby': 'tags-remove-on-delete-help' }"
            separator=" ,;"
            placeholder="Enter tracking numbers separated by space, comma or semicolon"
            no-add-on-enter
            add-on-change
            clearable
            @input="fetchDeclarationsData"
          />
        </b-form-group>
      </b-col>
      <b-col>
        <div class="d-flex justify-content-between">
          <b-form-group
            class="full-width"
            label-for="container-codes"
          >
            <b-form-tags
              id="container-codes"
              v-model="filterContainerCodes"
              label="Container codes"
              :input-attrs="{ 'aria-describedby': 'tags-remove-on-delete-help' }"
              separator=" ,;"
              placeholder="Enter container codes separated by space, comma or semicolon"
              no-add-on-enter
              add-on-change
              clearable
              @input="fetchDeclarationsData"
            />
          </b-form-group>
          <div>
            <b-button
              v-if="filtersApplied"
              id="filter-reset"
              variant="outline-secondary"
              class="ml-2"
              @click="resetFilters"
            >
              Reset
            </b-button>
          </div>
        </div>
      </b-col>
    </b-row>
    <b-row>
      <b-col>
        <b-form-group
          label-for="mrn-tree"
        >
          <b-form-tags
            id="mrn-tree"
            v-model="filterDeclarationsByMrn"
            label="MRN codes"
            :input-attrs="{ 'aria-describedby': 'tags-remove-on-delete-help' }"
            separator=" ,;"
            placeholder="Enter MRN codes separated by space, comma or semicolon"
            no-add-on-enter
            add-on-change
            clearable
            @input="fetchDeclarationsData"
          />
        </b-form-group>
      </b-col>
      <b-col>
        <b-form-group
          label-for="lrn-tree"
        >
          <b-form-tags
            id="lrn-tree"
            v-model="filterDeclarationsByLrn"
            label="LRN codes"
            :input-attrs="{ 'aria-describedby': 'tags-remove-on-delete-help' }"
            separator=" ,;"
            placeholder="Enter LRN codes separated by space, comma or semicolon"
            no-add-on-enter
            add-on-change
            clearable
            @input="fetchDeclarationsData"
          />
        </b-form-group>
      </b-col>
    </b-row>
    <b-table
      :items="flatTree"
      responsive
      :fields="fields"
      :sort-by.sync="sortBy"
      :sort-desc.sync="isSortDirDesc"
      no-local-sorting
      show-empty
      :busy="dataLoading"
      empty-text="No matching records found"
      class="mb-0 parcels-tree-table"
    >
      <template #table-busy>
        <div class="text-center my-2">
          <b-spinner class="align-middle mr-1" />
          <strong>{{ dataLoadingMessage }}...</strong>
        </div>
      </template>

      <template #head(declaration)="">
        <div
          v-if="canEdit"
          style="white-space: nowrap"
        >
          <b-form-checkbox
            v-if="!disableCheckbox"
            v-model="selectedAll"
            class="float-left select-all"
            @change="onChangeAll"
          />
          LRN
          (<span
            v-if="selectedDeclarations.length > 0"
            class="selected-items"
          >{{ selectedDeclarations.length }} selected / </span>{{ $attrs.declarations.length }})
        </div>
      </template>

      <template #cell(declaration)="data">
        <div
          v-if="typeof data.item.tracking_number === 'undefined' && data.item.id"
          style="white-space: nowrap"
        >
          <b-form-checkbox
            v-if="!disableCheckbox && canEdit"
            v-model="selectedDeclarations"
            :value="data.item.id"
            class="float-left"
            @change="onChangeDeclaration(data.item.id)"
          />
          <b-link
            v-b-tooltip.hover.right="'Parcels in shown status / total declaration parcels'"
            href="#"
            class="containerLink"
            @click="openCloseParcels(data.item.id)"
          >
            <feather-icon
              icon="ChevronRightIcon"
              size="18"
              :class="{
                'chevron-icon': true,
                'expanded': isOpened(data.item.id)
              }"
            />
            <span>{{ data.item.lrn }}</span>
            <span> ({{ data.item.goods_shipment_count }}/{{ totalGoodsShipments }})</span>
          </b-link>
          <copy-icon
            :text="data.item.lrn"
            tooltip-text="Copy LRN to clipboard"
            tooltip-success-text="LRN copied to clipboard"
          />
        </div>
      </template>

      <template #cell(tracking_number)="data">
        <div
          v-if="data.item.loading"
          class="text-center my-2"
        >
          <b-spinner class="align-middle mr-1" />
          <strong>Loading...</strong>
        </div>
        <div
          v-if="data.item.tracking_number"
          style="white-space: nowrap"
        >
          {{ data.item.tracking_number }}
        </div>
      </template>

      <template #cell(descriptions)="data">
        <div>
          {{ data.item.items.map(function (parcelItem) {return parcelItem.description}).join(', ') }}
        </div>
      </template>

      <template #cell(hs_codes)="data">
        <div>
          {{ data.item.items.map(function (parcelItem) {return parcelItem.hs_code}).join(', ') }}
        </div>
      </template>

      <template #cell(status_info)="data">
        <div v-if="hasErrors(data.item)">
          <span
            v-b-modal.modal-status-information
            class="text-primary text-nowrap"
            @click="informationItem = data.item"
          >
            Show errors
            <b-badge
              variant="light"
              class="text-primary"
            >
              {{ getErrorCount(data.item) }}
            </b-badge>
          </span>
        </div>
      </template>

      <template #cell(value)="data">
        <div
          v-if="typeof data.item.tracking_number !== 'undefined'"
          class="text-right"
        >
          {{ data.item.items.reduce(function (sum, parcelItem) {return sum + parseFloat(parcelItem.value)}, 0) | formatNumber(2) }}
        </div>
      </template>

      <template #cell(actions)="data">
        <div v-if="data.item.tracking_number">
          <b-button-group>
            <b-button
              v-if="canEdit && hasErrors(data.item) "
              v-b-modal.edit-parcel-items
              type="button"
              variant="warning"
              @click="$emit('edit-parcel', {
                declaration: data.item.low_value_declaration,
                parcel: data.item,
              })"
            >
              <feather-icon icon="EditIcon" />
            </b-button>
            <view-parcel-button
              v-else
              :declaration="data.item.low_value_declaration"
              :parcel="data.item"
            />
          </b-button-group>
        </div>
      </template>
    </b-table>
  </div>
</template>

<script>
import {
  BBadge,
  BButton,
  BButtonGroup,
  BCol,
  BFormCheckbox,
  BFormGroup,
  BFormTags,
  BLink,
  BRow,
  BSpinner,
  BTable,
  VBTooltip,
} from 'bootstrap-vue'

import ViewParcelButton from '@/views/shipments/declarations/components/ViewParcelButton.vue'
import { EventBus } from '@core/utils/utils'
import CopyIcon from '@/views/components/CopyIcon.vue'
import StatusInformation from '@/views/shipments/declarations/modals/StatusInformation.vue'

export default {
  components: {
    BBadge,
    CopyIcon,
    BButtonGroup,
    StatusInformation,
    BCol,
    BRow,
    BFormCheckbox,
    BFormGroup,
    BLink,
    BTable,
    BSpinner,
    BFormTags,
    BButton,
    ViewParcelButton,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  props: {
    disableCheckbox: Boolean,
    dataLoading: {
      type: Boolean,
      default: false,
    },
    showEdit: {
      type: Boolean,
      default: false,
    },
    dataLoadingMessage: {
      type: String,
      default: 'Loading',
    },
    shipment: {
      type: Object,
      default: () => {},
    },
    filters: {
      type: Object,
      default: () => {},
    },
    declarationType: {
      type: String,
      default: 'h7',
    },
    notAllowedFilters: {
      type: Array,
      default: () => [],
    },
    reloadParcels: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      loading: {},
      parcels: {},
      fullParcelsData: [],
      selectedDeclaration: null,
      selectedParcel: null,
      informationItem: null,
      arrived: null,
      sortBy: 'lrn',
      isSortDirDesc: false,
      selectedAll: false,
      selectedDeclarations: [],
      selectedParcels: [],
      openDeclarations: [],
      filterContainerCodes: [],
      filterDeclarationsByTrackingNumber: [],
      filterDeclarationsByMrn: [],
      filterDeclarationsByLrn: [],
    }
  },
  computed: {
    localFilters: {
      get() {
        return this.filters
      },
      set(newFilters) {
        this.$emit('update:filters', newFilters)
      },
    },
    canEdit() {
      return this.$permissions().hasPermission('edit shipment')
    },
    filtersApplied() {
      return this.filterContainerCodes.length > 0
        || this.filterDeclarationsByTrackingNumber.length > 0
        || this.filterDeclarationsByMrn.length > 0
        || this.filterDeclarationsByLrn.length > 0
    },
    fields() {
      return [
        {
          key: 'declaration',
          label: 'LRN',
          sortable: true,
        },
        {
          key: 'mrn',
          label: 'MRN',
          sortable: true,
        },
        {
          key: 'tracking_number',
          label: 'Tracking code',
          sortable: true,
        },
        {
          key: 'container_code',
          label: 'Container',
          sortable: true,
        },
        {
          key: 'descriptions',
          label: 'Description',
        },
        {
          key: 'hs_codes',
          label: 'HS code',
        },
        {
          key: 'status_info',
          label: 'Errors',
        },
        {
          key: 'actions',
          label: 'Actions',
        },
      ]
    },
    flatTree() {
      const { declarationsForView } = this
      const flatTree = []

      const declarations = Object.keys(declarationsForView)

      declarations.forEach(declarationKey => {
        const declaration = declarationsForView[declarationKey]
        const flatDeclaration = {
          id: declaration.id,
          lrn: declaration.lrn,
          mrn: declaration.mrn,
          goods_shipment_count: declaration.goods_shipment_count,
          status_info: declaration.status_info,
          items: [],
        }
        flatTree.push(flatDeclaration)

        const openedIndex = this.openDeclarations.indexOf(declaration.id)
        if (openedIndex === -1) {
          return
        }

        const parcels = this.parcels[declaration.id]
        if (typeof parcels === 'undefined') {
          flatTree.push({
            loading: this.loading[declaration.id],
            items: [],
          })
          return
        }

        // sort parcels so ones with errors come first
        const sortedParcels = [...parcels].sort((a, b) => {
          const aHasErrors = declaration.status_info?.errors?.some(
            e => String(e.declaration_value) === String(a.id),
          )
          const bHasErrors = declaration.status_info?.errors?.some(
            e => String(e.declaration_value) === String(b.id),
          )

          if (aHasErrors && !bHasErrors) return -1
          if (!aHasErrors && bHasErrors) return 1
          return 0
        })

        for (let i = 0; i < sortedParcels.length; i += 1) {
          const parcel = sortedParcels[i]
          const errors = declaration.status_info?.errors?.filter(
            e => String(e.declaration_value) === String(parcel.id),
          )
          flatTree.push({
            ...parcel,
            lrn: declaration.lrn,
            mrn: declaration.mrn,
            status_info: errors?.length ? { errors } : null,
          })
        }
      })

      return flatTree
    },
    declarationsForView() {
      const { declarations } = this.$attrs
      const declarationsForView = {}

      for (let i = 0; i < declarations.length; i += 1) {
        const declaration = declarations[i]

        declarationsForView[declaration.id] = {
          id: declaration.id,
          lrn: declaration.lrn,
          mrn: declaration.mrn,
          goods_shipment_count: declaration.goods_shipment?.goods_shipment_count,
          status_info: declaration.status_info,
          parcels: declaration.parcel,
        }
      }
      return declarationsForView
    },
    totalGoodsShipments() {
      return Object.values(this.declarationsForView).reduce((sum, declaration) => sum + (declaration.goods_shipment_count || 0), 0)
    },
  },
  watch: {
    filters() {
      this.parcels = {}
    },
    filterContainerCodes() {
      this.parcels = {}
      this.openDeclarations.forEach(id => {
        this.requestParcels(id)
      })
    },
    filterDeclarationsByTrackingNumber() {
      this.parcels = {}
      this.openDeclarations.forEach(id => {
        this.requestParcels(id)
      })
    },
    filterDeclarationsByMrn() {
      this.parcels = {}
      this.openDeclarations.forEach(id => {
        this.requestParcels(id)
      })
    },
    filterDeclarationsByLrn() {
      this.parcels = {}
      this.openDeclarations.forEach(id => {
        this.requestParcels(id)
      })
    },
    sortBy() {
      if (this.sortBy !== 'lrn') {
        this.openDeclarations.forEach(id => {
          this.requestParcels(id)
        })
      }
    },
    isSortDirDesc() {
      if (this.sortBy === 'lrn') {
        this.$emit('sortDesc', this.isSortDirDesc)
      } else {
        this.openDeclarations.forEach(id => {
          this.requestParcels(id)
        })
      }
    },
    reloadParcels() {
      this.openDeclarations.forEach(id => {
        this.loading[id] = true
        this.requestParcels(id)
      })
    },
  },
  mounted() {
    EventBus.$emit('handle-filters-visibility', false)
  },
  created() {
    EventBus.$on('update-parcel-items', this.handleUpdateParcelItems)
    EventBus.$on('clear-selections', this.tableClearSelections)
  },
  beforeDestroy() {
    EventBus.$off('update-parcel-items', this.handleUpdateParcelItems)
    EventBus.$off('clear-selections', this.tableClearSelections)
  },
  methods: {
    hasErrors(item) {
      try {
        const info = typeof item.status_info === 'string'
          ? JSON.parse(item.status_info)
          : item.status_info

        return info && Array.isArray(info.errors) && info.errors.length > 0
      } catch (e) {
        return false
      }
    },
    getErrorCount(item) {
      try {
        const info = typeof item.status_info === 'string'
          ? JSON.parse(item.status_info)
          : item.status_info

        return Array.isArray(info.errors) ? info.errors.length : 0
      } catch (e) {
        return 0
      }
    },
    handleUpdateParcelItems() {
      this.selectedParcel = null
    },
    resetFilters() {
      this.resetSelections()
      this.filterContainerCodes = []
      this.filterDeclarationsByTrackingNumber = []
      this.filterDeclarationsByMrn = []
      this.filterDeclarationsByLrn = []
    },
    tableClearSelections() {
      this.selectedAll = true
      this.selectedAll = false
      this.parcels = {}
      this.openDeclarations = []
      this.selectedDeclarations = []
    },
    resetSelections() {
      this.tableClearSelections()
      this.propagateSelection()
    },
    requestParcels(id) {
      this.$http.get(`/v1/shipments/${this.shipment.id}/declarations/${id}/parcels`, {
        params: {
          tracking_number: this.filterDeclarationsByTrackingNumber.join(','),
          container_code: this.filterContainerCodes.join(','),
          mrn: this.filterDeclarationsByMrn.join(','),
          lrn: this.filterDeclarationsByLrn.join(','),
          sortBy: this.sortBy !== 'lrn' ? this.sortBy : null,
          sortDesc: this.isSortDirDesc,
        },
      })
        .then(response => {
          this.loading[id] = false
          const temp = this.parcels
          temp[id] = response.data.data
          this.parcels = null
          this.parcels = temp
          this.fullParcelsData.push(temp)
          this.$emit('parcels', {
            fullParcelsData: this.fullParcelsData,
          })
          this.applyChangeDeclaration(id)
        })
    },
    viewParcel(declaration, parcel) {
      this.selectedDeclaration = declaration
      this.selectedParcel = parcel
    },
    openCloseParcels(id) {
      const openedIndex = this.openDeclarations.indexOf(id)
      if (openedIndex === -1) {
        this.openDeclarations.push(id)
        if (!this.parcels[id]) {
          this.loading[id] = true
          this.requestParcels(id)
        }
      } else {
        this.openDeclarations.splice(openedIndex, 1)
      }
    },
    onChangeAll() {
      this.applyChangeAll()
      this.propagateSelection()
    },
    onChangeDeclaration(declarationId) {
      this.applyChangeDeclaration(declarationId)
      this.propagateSelection()
    },
    applyChangeAll() {
      const declarations = this.declarationsForView

      Object.keys(declarations).forEach(id => {
        const numericId = Number(id)
        const isFiltered = true
        const isAlreadySelected = this.selectedDeclarations.includes(numericId)

        if (this.selectedAll && isFiltered) {
          if (!isAlreadySelected) {
            this.selectedDeclarations.push(numericId)
          }
        } else if (!this.selectedAll && isAlreadySelected) {
          const index = this.selectedDeclarations.indexOf(numericId)
          if (index !== -1) {
            this.selectedDeclarations.splice(index, 1)
          }
        }
        this.applyChangeDeclaration(numericId, false)
      })
    },
    applyChangeDeclaration(id) {
      const parcels = this.parcels[id]

      if (typeof parcels === 'undefined') {
        return
      }

      Object.keys(parcels).forEach(parcelKey => {
        const trackingNumber = parcels[parcelKey].tracking_number
        const parcelIndex = this.selectedParcels.indexOf(trackingNumber)

        if (this.selectedDeclarations.indexOf(id) !== -1) {
          if (parcelIndex === -1) {
            this.selectedParcels.push(trackingNumber)
          }
        } else if (parcelIndex !== -1) {
          this.selectedParcels.splice(parcelIndex, 1)
        }
      })
    },
    propagateSelection() {
      const allSelected = this.selectedAll
      this.$emit('selection', {
        selectedAll: allSelected,
        selectedDeclarations: this.selectedDeclarations,
        selectedParcels: this.selectedParcels,
      })
    },
    isOpened(declarationId) {
      return this.openDeclarations.indexOf(declarationId) !== -1
    },
    fetchDeclarationsData() {
      this.openDeclarations = []
      this.parcels = []
      this.localFilters = {
        container_code: this.filterContainerCodes,
        tracking_number: this.filterDeclarationsByTrackingNumber,
        mrn: this.filterDeclarationsByMrn,
        lrn: this.filterDeclarationsByLrn,
      }
    },
  },
}
</script>
<style>
  #filter-reset {
    padding-top: .63rem;
    padding-bottom: .63rem;
  }
  .chevron-icon {
    transition: all 0.3s;
  }
  .chevron-icon.expanded {
    transform: rotate(90deg);
  }
</style>
